import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import { useOrganisationRecord } from "@/app/Organisations/Hooks/useOrganisationRecord";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import {
  OrganisationSupplierItem,
  organisationSuppliersQuery,
} from "@/app/Organisations/GraphQL/organisationSuppliersQuery";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";

type ColumnId =
  | "name"
  | "booking_fee"
  | "charge_per_move"
  | "current_listings_count"
  | "created_at";

const columns: DataTableColDef<OrganisationSupplierItem, any, ColumnId>[] = [
  {
    id: "name",
    accessorFn: (row) => row.name,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => (
      <ModelLinkCell
        from="/$tenant/$orgId/supplier/{-$supplierId}"
        to="/$tenant/$orgId/supplier/{-$supplierId}/settings"
        params={(prev) => prev}
        search={{ supplier: row.original.id }}
      >
        {row.original.name}
      </ModelLinkCell>
    ),
  },
  {
    id: "booking_fee",
    accessorFn: (row) =>
      formatCurrency(row.booking_fee_amount, row.currency, { showZero: true }),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Booking fee" />
    ),
    cell: DeEmphasisedCell,
  },
  {
    id: "charge_per_move",
    accessorFn: (row) =>
      formatCurrency(row.charge_per_move_amount, row.currency, {
        showZero: true,
      }),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Charge per move" />
    ),
    cell: DeEmphasisedCell,
  },
  {
    id: "current_listings_count",
    accessorFn: (row) => row.current_listings_count ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Current listings" />
    ),
    cell: DeEmphasisedCell,
  },
  {
    id: "created_at",
    accessorFn: (row) => row.created_at,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => <TimeSinceNow time={row.original.created_at} />,
  },
];

export function OrganisationSuppliersScreen() {
  const record = useOrganisationRecord();

  return (
    <div className="py-6">
      <DataTable
        id="organisation-suppliers"
        columns={columns}
        document={organisationSuppliersQuery}
        accessor={(res) => res.suppliers}
        getQueryVariables={({ pagination }) => ({
          managed_by_id: record.id,
          page: pagination.pageIndex,
          first: pagination.pageSize,
        })}
        searchable={false}
        columnToggles={false}
      />
    </div>
  );
}
